import React, { useCallback, useEffect, useRef, useState } from "react";
import { consoleApi, triggerBlobDownload, downloadImageFromUrl } from "../../../services/consoleApiService";

function classify(error) {
  return error?.response?.data?.detail || error?.message || "Something went wrong";
}

/** Short "Jun 5" date from an ISO string, or "". */
function fmtPubDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const STALE_HOURS = 48;

/** Hours since an ISO timestamp, or null. */
function hoursSince(iso) {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return (Date.now() - t) / 3_600_000;
}

/** Human "3 days" / "5 hours" from an hour count. */
function humanAge(hours) {
  if (hours == null) return "";
  if (hours < 1) return "under an hour";
  if (hours < 24) return `${Math.round(hours)} hour${Math.round(hours) === 1 ? "" : "s"}`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"}`;
}

const PLATFORM_VIEWPORTS = [
  { label: "iPhone 14 Pro (390x844)", w: 390, h: 844 },
  { label: "iPhone SE (375x667)", w: 375, h: 667 },
  { label: "Pixel 7 (412x915)", w: 412, h: 915 },
];

const KNOWN_ROUTES = [
  "/",
  "/explore",
  "/clubs",
  "/runs",
  "/login",
  "/console/overview",
];

const MOOD_OPTIONS = [
  { value: "night-tempo", label: "Night Tempo", swatch: "#787f9a" },
  { value: "steady-slate", label: "Steady Slate", swatch: "#3d4256" },
  { value: "verified-forest", label: "Verified Forest", swatch: "#3a6a4c" },
  { value: "warning-dusk", label: "Warning Dusk", swatch: "#f59e0b" },
];
const MOOD_BY_VALUE = Object.fromEntries(MOOD_OPTIONS.map((m) => [m.value, m]));

export default function BriefDetail({ briefId, onChanged, onOpenBrief, onDeleted }) {
  const [brief, setBrief] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [renderMsg, setRenderMsg] = useState({}); // per-slide re-render feedback, keyed by slide no
  const [variationCount, setVariationCount] = useState(3); // how many to generate
  const [varMsg, setVarMsg] = useState(null); // variation-generation feedback
  // Auto-spread: post one variation per account in a single pass.
  const [spreadOpen, setSpreadOpen] = useState(false);
  const [loadingSpread, setLoadingSpread] = useState(false);
  const [spreadAccounts, setSpreadAccounts] = useState([]);
  const [spreadUnits, setSpreadUnits] = useState([]); // [{id,label,mood,caption,renders,account_id,publications}]
  const [spreadBusy, setSpreadBusy] = useState(false);
  const [spreadResults, setSpreadResults] = useState(null);
  const [spreadAsReel, setSpreadAsReel] = useState(false); // spread posts the reel (w/ music) vs the carousel
  const [reelMsg, setReelMsg] = useState(null); // motion-reel build feedback
  const [reelAccounts, setReelAccounts] = useState([]); // connected accounts for the reel "post to account" picker
  const [reelAccountId, setReelAccountId] = useState(""); // account chosen to post the reel to
  const [reelBusy, setReelBusy] = useState(false); // reel post in flight
  const [animateMsg, setAnimateMsg] = useState({}); // per-slide animate feedback
  const [audioTrack, setAudioTrack] = useState("auto"); // reel music: 'auto' | 'none' | '<vibe/file>'
  const [audioVibes, setAudioVibes] = useState([]); // [{name,label,tracks:[{id,label}]}] for the picker
  const [previewId, setPreviewId] = useState(null); // track id currently auditioning, or null
  const audioElRef = useRef(null);    // hidden <audio> for the picker preview
  const previewUrlRef = useRef(null); // object URL to revoke between previews

  const [captureRoute, setCaptureRoute] = useState("/explore");
  const [captureViewport, setCaptureViewport] = useState(PLATFORM_VIEWPORTS[0]);
  const [captureLabel, setCaptureLabel] = useState("");

  const [stockLib, setStockLib] = useState(null);
  const [stockCategory, setStockCategory] = useState(null);
  const [pendingStockUrl, setPendingStockUrl] = useState(null);
  const [pendingStockLabel, setPendingStockLabel] = useState("");

  // Live-data re-targeting (set/change a brief's city after creation)
  const [cities, setCities] = useState([]);

  // UI: body sub-tabs, caption platform, render lightbox
  const [bodyTab, setBodyTab] = useState("copy"); // 'copy' | 'assets' | 'renders'
  const [captionTab, setCaptionTab] = useState("instagram");
  const [lightboxRender, setLightboxRender] = useState(null);

  // Screenshots-bank picker (lives in the Assets tab)
  const [bankOpen, setBankOpen] = useState(false);
  const [bankItems, setBankItems] = useState(null);
  const [bankCategory, setBankCategory] = useState("");

  // Mobile-app capture (iOS Simulator)
  const [mobileStatus, setMobileStatus] = useState(null); // {available,booted,app_installed,device}
  const [mobileScreen, setMobileScreen] = useState("auto");
  const [mobileAttachSlide, setMobileAttachSlide] = useState("");

  const loadBank = useCallback(async () => {
    try {
      const data = await consoleApi.content.listScreenshots();
      setBankItems(Array.isArray(data) ? data : []);
    } catch {
      setBankItems([]);
    }
  }, []);

  const handleOpenBank = () => {
    setBankOpen((open) => {
      const next = !open;
      if (next && bankItems === null) loadBank();
      return next;
    });
  };

  const handleAttachFromBank = (screenshotId) =>
    runWithBusy("attaching", async () => {
      await consoleApi.content.attachScreenshotAsAsset(briefId, screenshotId);
      await load();
      onChanged?.();
    });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await consoleApi.content.getBrief(briefId);
      setBrief(data);
    } catch (e) {
      setError(classify(e));
    } finally {
      setLoading(false);
    }
  }, [briefId]);

  useEffect(() => {
    load();
  }, [load]);

  // Fetch stock library once per mount
  useEffect(() => {
    let alive = true;
    consoleApi.content
      .stockLibrary()
      .then((data) => {
        if (!alive) return;
        setStockLib(data);
        if (data?.categories?.length) {
          setStockCategory(data.categories[0].name);
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Fetch the reel music library once per mount (auto-discovered tracks by vibe)
  useEffect(() => {
    let alive = true;
    consoleApi.content
      .listAudio()
      .then((data) => {
        if (!alive) return;
        setAudioVibes(Array.isArray(data?.vibes) ? data.vibes : []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Load cities once for the re-target dropdown
  useEffect(() => {
    let alive = true;
    consoleApi.cities
      .list()
      .then((data) => {
        if (!alive) return;
        setCities(Array.isArray(data) ? data : data?.cities || []);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  // Probe whether mobile-app capture is available (sim booted + app installed)
  const refreshMobileStatus = useCallback(async () => {
    try {
      const s = await consoleApi.content.mobileCaptureStatus();
      setMobileStatus(s);
    } catch {
      setMobileStatus({ available: false, booted: false, app_installed: false });
    }
  }, []);

  useEffect(() => {
    refreshMobileStatus();
  }, [refreshMobileStatus]);

  const handleStockClick = (item) => {
    setPendingStockUrl(item.url);
    setPendingStockLabel(item.label || item.filename);
  };

  const handleAttachStockToSlide = (slideIdx) => {
    if (!pendingStockUrl) return;
    setBrief((prev) => {
      if (!prev) return prev;
      const slides = [...(prev.slides || [])];
      const target = slides[slideIdx];
      if (!target) return prev;
      const data = { ...(target.template_data || {}) };
      if ("screenshot" in data) data.screenshot = pendingStockUrl;
      else if ("bg" in data) data.bg = pendingStockUrl;
      else data.screenshot = pendingStockUrl;
      slides[slideIdx] = { ...target, template_data: data };
      return { ...prev, slides };
    });
    setPendingStockUrl(null);
    setPendingStockLabel("");
  };

  const cancelStockAttach = () => {
    setPendingStockUrl(null);
    setPendingStockLabel("");
  };

  const runWithBusy = async (label, fn) => {
    setBusy(label);
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError(classify(e));
    } finally {
      setBusy(null);
    }
  };

  const handleGenerate = (regenerate) =>
    runWithBusy(regenerate ? "regenerating" : "generating", async () => {
      const updated = await consoleApi.content.generateCopy(briefId, { regenerate });
      setBrief(updated);
      onChanged?.();
    });

  const handleSlideEdit = (slideIdx, field, value) => {
    setBrief((prev) => {
      if (!prev) return prev;
      const slides = [...(prev.slides || [])];
      const target = slides[slideIdx];
      if (!target) return prev;
      const data = { ...(target.template_data || {}) };
      // Field may be dot-path: e.g. "slide1.headline" — but we already have the slide index.
      // We expect a top-level data key here (e.g. "headline", "subhead").
      data[field] = value;
      slides[slideIdx] = { ...target, template_data: data };
      return { ...prev, slides };
    });
  };

  const handleGenerateImage = async (slideNo, prompt) => {
    setBusy(`gen-image-${slideNo}`);
    setRenderMsg((m) => ({ ...m, [slideNo]: null }));
    try {
      const updated = await consoleApi.content.generateImage(briefId, {
        prompt,
        slide_index: slideNo,
        aspect_ratio: "3:4",
      });
      // Endpoint returns the full brief when slide_index is set; an asset otherwise.
      if (updated && updated.slides) setBrief(updated);
      else await load();
      onChanged?.();
    } catch (e) {
      // Surfaced inline at the slide (the global banner sits far above the list).
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: false, text: classify(e) } }));
      setBusy(null);
      return;
    }
    // The new image is now the slide's background — bake it into the render so it
    // actually shows up (generate updates the bg; the render rasterizes it).
    try {
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: true, text: "Image generated — rendering…" } }));
      const rendered = await consoleApi.content.render(briefId, {
        slide_indexes: [slideNo],
        force: true,
      });
      setBrief(rendered);
      onChanged?.();
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: true, text: "Image generated & rendered ✓" } }));
      setTimeout(() => setRenderMsg((m) => ({ ...m, [slideNo]: null })), 5000);
    } catch (e) {
      setRenderMsg((m) => ({
        ...m,
        [slideNo]: { ok: false, text: `Image generated ✓ but render failed: ${classify(e)}` },
      }));
    } finally {
      setBusy(null);
    }
  };

  // Generate Higgsfield images for every slide that has a (non-empty) prompt.
  // Sequential so we respect rate limits; updates the brief after each.
  const handleGenerateAllImages = async () => {
    const prompts = brief?.higgsfield_prompts || [];
    const targets = (brief?.slides || [])
      .map((s, idx) => {
        const data = s.template_data || {};
        // Phone-mockup slides use real app screenshots, not Higgsfield lifestyle
        // photos — skip them so we don't waste a generation or break the mockup.
        const isPhoneSlide = "screenshot" in data && !("bg" in data);
        return {
          slideIndex: s.index || idx + 1,
          prompt: (prompts[idx] || "").trim(),
          isPhoneSlide,
        };
      })
      .filter((t) => t.prompt.length > 0 && !t.isPhoneSlide);
    if (targets.length === 0) return;
    setError(null);
    for (let i = 0; i < targets.length; i++) {
      setBusy(`gen-all ${i + 1}/${targets.length}`);
      try {
        const updated = await consoleApi.content.generateImage(briefId, {
          prompt: targets[i].prompt,
          slide_index: targets[i].slideIndex,
          aspect_ratio: "3:4",
        });
        if (updated && updated.slides) setBrief(updated);
      } catch (e) {
        setError(classify(e));
        break; // stop the batch on first failure (likely auth/credits)
      }
    }
    setBusy(null);
    await load();
    onChanged?.();
  };

  const handleSaveSlides = () =>
    runWithBusy("saving", async () => {
      const updated = await consoleApi.content.updateBrief(briefId, {
        slides: brief.slides,
        captions: brief.captions,
      });
      setBrief(updated);
      onChanged?.();
    });

  const handleMoodChange = (nextMood) =>
    runWithBusy("saving", async () => {
      const updated = await consoleApi.content.updateBrief(briefId, { mood: nextMood });
      setBrief(updated);
      onChanged?.();
    });

  const handleCityTargetChange = (cityId) =>
    runWithBusy("saving", async () => {
      // Changing the city clears club/run targeting (they belonged to the old city).
      const updated = await consoleApi.content.updateBrief(briefId, {
        target_city_id: cityId || null,
        target_club_id: null,
        target_meeting_id: null,
      });
      setBrief(updated);
      onChanged?.();
    });

  const handleCaptionEdit = (platform, value) => {
    setBrief((prev) => {
      const captions = { ...(prev.captions || {}) };
      captions[platform] = value;
      return { ...prev, captions };
    });
  };

  const handleUpload = (file) =>
    runWithBusy("uploading", async () => {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "higgsfield");
      fd.append("label", file.name);
      await consoleApi.content.uploadAsset(briefId, fd);
      await load();
      onChanged?.();
    });

  const handleCapture = () =>
    runWithBusy("capturing", async () => {
      await consoleApi.content.capture(briefId, {
        route: captureRoute,
        viewport_w: captureViewport.w,
        viewport_h: captureViewport.h,
        label: captureLabel || `capture ${captureRoute}`,
      });
      await load();
      onChanged?.();
    });

  const handleCaptureMobile = () =>
    runWithBusy("capturing-mobile", async () => {
      const body = {};
      if (mobileScreen && mobileScreen !== "auto") body.screen = mobileScreen;
      if (mobileAttachSlide) body.attach_slide_index = Number(mobileAttachSlide);
      const result = await consoleApi.content.captureMobile(briefId, body);
      if (result?.brief) setBrief(result.brief);
      await load();
      onChanged?.();
      refreshMobileStatus();
    });

  // Capture a whole set of relevant screens in one pass (derived from the brief target).
  const handleCaptureMobileSet = () =>
    runWithBusy("capturing-mobile-set", async () => {
      const result = await consoleApi.content.captureMobileBatch(briefId, {});
      await load();
      onChanged?.();
      refreshMobileStatus();
      if (result?.errors?.length) {
        setError(
          `Captured ${result.captured?.length || 0}; ${result.errors.length} couldn't load: ` +
            result.errors.map((e) => e.screen).join(", ")
        );
      }
    });

  const handleRender = () =>
    runWithBusy("rendering", async () => {
      const updated = await consoleApi.content.render(briefId, {});
      setBrief(updated);
      onChanged?.();
    });

  // Animate a slide's hero photo into a cinematic clip (Higgsfield, spends credits).
  // 5s is the default one-click; a 10s clip costs ~2× the credits, so it asks first
  // and sends the explicit opt-in the backend requires (confirm_extended_duration).
  const handleAnimateSlide = (slideNo, duration = 5) => {
    const extended = duration > 5;
    const ok = window.confirm(
      extended
        ? `Animate slide ${slideNo}'s hero into a ~${duration}s clip?\n\n` +
          `Longer clips cost about 2× the credits (~14) and take a minute or two.`
        : `Animate slide ${slideNo}'s hero photo into a ~5s cinematic clip?\n\n` +
          `This uses Higgsfield credits (~7) and takes about a minute.`
    );
    if (!ok) return;
    const body = extended
      ? { duration, confirm_extended_duration: true }
      : { duration };
    setBusy(`animate-${slideNo}`);
    setAnimateMsg((m) => ({ ...m, [slideNo]: null }));
    consoleApi.content
      .animateSlide(briefId, slideNo, body)
      .then((updated) => {
        setBrief(updated);
        onChanged?.();
        setAnimateMsg((m) => ({ ...m, [slideNo]: { ok: true, text: "Clip ready ✓" } }));
        setTimeout(() => setAnimateMsg((m) => ({ ...m, [slideNo]: null })), 6000);
      })
      .catch((e) => setAnimateMsg((m) => ({ ...m, [slideNo]: { ok: false, text: classify(e) } })))
      .finally(() => setBusy(null));
  };

  // Build a 9:16 motion reel (MP4) from the rendered slides — free, no AI credits.
  // Preview/audition the selected music track's hook right in the picker.
  const togglePreview = async () => {
    const el = audioElRef.current;
    if (!el || !audioTrack || audioTrack === "auto" || audioTrack === "none") return;
    if (previewId === audioTrack && !el.paused) {
      el.pause();
      setPreviewId(null);
      return;
    }
    try {
      const blob = await consoleApi.content.getAudioBlob(audioTrack);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = URL.createObjectURL(blob);
      el.src = previewUrlRef.current;
      await el.play();
      setPreviewId(audioTrack);
    } catch {
      setPreviewId(null);
    }
  };

  // Stop preview when the selection changes; revoke the object URL on unmount.
  useEffect(() => {
    const el = audioElRef.current;
    if (el && !el.paused) el.pause();
    setPreviewId(null);
  }, [audioTrack]);
  useEffect(
    () => () => {
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    },
    []
  );

  const handleMakeReel = () =>
    runWithBusy("reel", async () => {
      setReelMsg(null);
      const updated = await consoleApi.content.generateReel(briefId, { audio_track: audioTrack });
      setBrief(updated);
      onChanged?.();
      const music = updated?.reel?.audio_label;
      setReelMsg(music ? `Reel ready ✓ · ♪ ${music}` : "Reel ready ✓");
      setTimeout(() => setReelMsg(null), 6000);
    });

  // Generate N linked variations (reworded copy + rotated mood), each rendered
  // and ready to post to its own account. Refreshes the list so the new
  // variation briefs appear, and the detail so the Variations strip fills in.
  const handleGenerateVariations = () =>
    runWithBusy("variations", async () => {
      setVarMsg(null);
      const res = await consoleApi.content.generateVariations(briefId, {
        count: variationCount,
        vary_mood: true,
      });
      await load();
      onChanged?.();
      const n = res?.count ?? (res?.variations?.length || 0);
      setVarMsg(`Generated ${n} variation${n === 1 ? "" : "s"} ✓`);
      setTimeout(() => setVarMsg(null), 6000);
    });

  // Open the spread panel: load connected accounts + each unit's full renders +
  // caption (the original counts as the first unit), and auto-pair by order.
  const captionOf = (b) =>
    b?.captions?.instagram || Object.values(b?.captions || {}).find((v) => typeof v === "string" && v) || "";

  // Connected accounts for the reel "post to account" picker (same source as the
  // carousel spread). Loaded lazily once a reel exists.
  const loadReelAccounts = useCallback(async () => {
    try {
      const accts = await consoleApi.content.studioAccounts();
      setReelAccounts(Array.isArray(accts) ? accts : []);
    } catch {
      setReelAccounts([]); // picker shows "no accounts"; the post button stays disabled
    }
  }, []);

  useEffect(() => {
    if (brief?.reel?.url && reelAccounts.length === 0) loadReelAccounts();
  }, [brief?.reel?.url, reelAccounts.length, loadReelAccounts]);

  // Post the built reel (MP4) to one connected account as a single video post
  // (Instagram Reel). Tied to this brief via brief_id so the "Already published"
  // badge + dup-guard cover reels just like the carousel spread.
  const handlePostReel = async () => {
    const url = brief?.reel?.url;
    if (!url) return;
    if (!reelAccountId) {
      setError("Pick an account to post the reel to.");
      return;
    }
    const acct = reelAccounts.find((a) => String(a.id) === String(reelAccountId));
    const already = (brief.publications || []).some(
      (p) => String(p.postbridge_account_id) === String(reelAccountId)
    );
    const dupWarn = already ? "\n\n⚠ This brief was already posted to that account." : "";
    if (!window.confirm(`Post this reel to @${acct?.username || reelAccountId} on real Instagram?${dupWarn}`)) return;

    setReelBusy(true);
    setReelMsg(null);
    setError(null);
    try {
      const fd = new FormData();
      fd.append("media_urls", JSON.stringify([{ url, mime_type: "video/mp4", name: `${briefId}-reel.mp4` }]));
      fd.append("account_ids", JSON.stringify([reelAccountId]));
      fd.append("caption", captionOf(brief) || "");
      fd.append("brief_id", briefId);
      const res = await consoleApi.content.studioPost(fd);
      const r0 = (res?.results || [])[0] || {};
      const status = r0.status || (res?.posted ? "posted" : "failed");
      if (status === "posted" || status === "scheduled") {
        setReelMsg(`Reel ${status === "scheduled" ? "scheduled" : "posted"} to @${acct?.username || "account"} ✓`);
        await load(); // refresh the "Already published" badge
        onChanged?.();
      } else {
        setError(r0.error || "Reel post failed.");
      }
    } catch (e) {
      setError(classify(e));
    } finally {
      setReelBusy(false);
      setTimeout(() => setReelMsg(null), 6000);
    }
  };

  const openSpread = async () => {
    setSpreadOpen(true);
    setLoadingSpread(true);
    setSpreadResults(null);
    setSpreadAsReel(!!brief.reel?.url); // default to reels when this brief already has one
    try {
      const accts = await consoleApi.content.studioAccounts();
      const acctList = Array.isArray(accts) ? accts : [];
      setSpreadAccounts(acctList);
      const variationBriefs = await Promise.all(
        (brief.variations || []).map((v) => consoleApi.content.getBrief(v.id).catch(() => null))
      );
      const all = [brief, ...variationBriefs.filter(Boolean)];
      setSpreadUnits(
        all.map((b, i) => {
          const renders = [...(b.renders || [])].sort((x, y) => (x.slide_index || 0) - (y.slide_index || 0));
          return {
            id: b.id,
            label: b.variation_label || (b.variation_of ? "Variation" : "Original"),
            mood: b.mood,
            caption: captionOf(b),
            renders,
            reel: b.reel || null, // present if a music reel was already built for this unit
            publications: b.publications || [],
            account_id: acctList[i]?.id || "", // auto-assign one per account, in order
          };
        })
      );
    } catch (e) {
      setError(classify(e));
      setSpreadOpen(false);
    } finally {
      setLoadingSpread(false);
    }
  };

  const setUnitAccount = (unitId, accountId) =>
    setSpreadUnits((prev) => prev.map((u) => (u.id === unitId ? { ...u, account_id: accountId } : u)));

  // Post each assigned unit to its account (sequential REAL posts). brief_id ties
  // each post to its variation so the posted badges update + dup-guard works.
  const doSpread = async () => {
    const ready = spreadUnits.filter(
      (u) => u.account_id && (spreadAsReel ? u.reel?.url || u.renders.length : u.renders.length)
    );
    if (!ready.length) {
      setError("Assign at least one variation to an account first.");
      return;
    }
    // Guard: warn if two units target the same account (defeats the point).
    const acctCounts = ready.reduce((m, u) => ({ ...m, [u.account_id]: (m[u.account_id] || 0) + 1 }), {});
    const dupe = Object.values(acctCounts).some((c) => c > 1);
    const lines = ready.map((u) => {
      const a = spreadAccounts.find((x) => x.id === u.account_id);
      return `${u.label} → @${a?.username || u.account_id}`;
    });
    const warn = dupe ? "\n\n⚠ Two variations target the same account." : "";
    const kind = spreadAsReel ? "reels (with music)" : "carousels";
    if (!window.confirm(`Post these ${kind} to real Instagram accounts?\n\n${lines.join("\n")}${warn}`)) return;

    setSpreadBusy(true);
    setSpreadResults(null);
    const results = [];
    for (const u of ready) {
      const a = spreadAccounts.find((x) => x.id === u.account_id);
      const idx = results.length;
      results.push({ unit: u.label, username: a?.username, status: "working" });
      setSpreadResults([...results]);
      try {
        let media;
        if (spreadAsReel) {
          // Post the reel (music baked in). Build it on the fly if this variation
          // doesn't have one yet — auto-picks a track by its mood.
          let reelUrl = u.reel?.url;
          if (!reelUrl) {
            results[idx] = { ...results[idx], status: "building" };
            setSpreadResults([...results]);
            const built = await consoleApi.content.generateReel(u.id, { audio_track: "auto" });
            reelUrl = built?.reel?.url;
          }
          if (!reelUrl) throw new Error("reel build failed");
          media = [{ url: reelUrl, mime_type: "video/mp4", name: `${u.id}-reel.mp4` }];
        } else {
          media = u.renders.map((r) => ({ url: r.url, name: `${u.id}-slide${r.slide_index || 0}.png` }));
        }
        const fd = new FormData();
        fd.append("media_urls", JSON.stringify(media));
        fd.append("account_ids", JSON.stringify([u.account_id]));
        fd.append("caption", u.caption || "");
        fd.append("brief_id", u.id);
        const res = await consoleApi.content.studioPost(fd);
        const r0 = (res?.results || [])[0] || {};
        results[idx] = { unit: u.label, username: a?.username, status: r0.status || (res?.posted ? "posted" : "failed"), error: r0.error };
      } catch (e) {
        results[idx] = { unit: u.label, username: a?.username, status: "failed", error: classify(e) };
      }
      setSpreadResults([...results]);
    }
    setSpreadBusy(false);
    await load(); // refresh posted badges on the variation cards
    onChanged?.();
  };

  // Re-render a single slide. Forces a fresh render (bypasses the content-hash
  // cache) so an explicit click always re-renders, and reports the result INLINE
  // at the slide — the global error/busy banners sit far above the slide list, so
  // without this a failure (or an unchanged-image no-op) looks like "nothing
  // happened". Other slots are preserved by the backend.
  const handleRenderSlide = async (slideNo) => {
    setBusy(`render-slide-${slideNo}`);
    setRenderMsg((m) => ({ ...m, [slideNo]: null }));
    try {
      const updated = await consoleApi.content.render(briefId, {
        slide_indexes: [slideNo],
        force: true,
      });
      setBrief(updated);
      onChanged?.();
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: true, text: "Re-rendered ✓" } }));
      setTimeout(() => setRenderMsg((m) => ({ ...m, [slideNo]: null })), 4000);
    } catch (e) {
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: false, text: classify(e) } }));
    } finally {
      setBusy(null);
    }
  };

  // Save this slide's copy edits (persists the whole brief's current copy) with
  // inline per-slide feedback.
  const handleSaveSlide = async (slideNo) => {
    setBusy(`save-slide-${slideNo}`);
    setRenderMsg((m) => ({ ...m, [slideNo]: null }));
    try {
      const updated = await consoleApi.content.updateBrief(briefId, {
        slides: brief.slides,
        captions: brief.captions,
      });
      setBrief(updated);
      onChanged?.();
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: true, text: "Saved ✓" } }));
      setTimeout(() => setRenderMsg((m) => ({ ...m, [slideNo]: null })), 3000);
    } catch (e) {
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: false, text: classify(e) } }));
    } finally {
      setBusy(null);
    }
  };

  // Regenerate a single copy field on a slide with AI (independent per field).
  const handleRegenerateField = async (slideNo, field) => {
    setBusy(`field-${slideNo}-${field}`);
    setRenderMsg((m) => ({ ...m, [slideNo]: null }));
    try {
      const updated = await consoleApi.content.regenerateField(briefId, slideNo, { field });
      setBrief(updated);
      onChanged?.();
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: true, text: `Regenerated ${field} ✓` } }));
      setTimeout(() => setRenderMsg((m) => ({ ...m, [slideNo]: null })), 3000);
    } catch (e) {
      setRenderMsg((m) => ({ ...m, [slideNo]: { ok: false, text: classify(e) } }));
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = () =>
    runWithBusy("deleting", async () => {
      if (!window.confirm("Delete this brief? Assets in GCP stay; only the DB record is removed.")) {
        return;
      }
      await consoleApi.content.deleteBrief(briefId);
      onDeleted?.();
    });

  const handleAttachAssetToSlide = (slideIdx, assetId) => {
    setBrief((prev) => {
      const slides = [...(prev.slides || [])];
      const asset = (prev.assets || []).find((a) => a.id === assetId);
      const url = asset?.url || "";
      const target = slides[slideIdx];
      if (!target) return prev;
      const data = { ...(target.template_data || {}) };
      // Heuristic: if there's a screenshot field already, fill it; otherwise fill bg.
      if ("screenshot" in data) {
        data.screenshot = url;
      } else if ("bg" in data) {
        data.bg = url;
      } else {
        // Default to screenshot — most templates expect it
        data.screenshot = url;
      }
      slides[slideIdx] = { ...target, template_data: data, asset_id: assetId };
      return { ...prev, slides };
    });
  };

  if (loading) return <div className="content-hub__empty"><p>Loading brief…</p></div>;
  if (error && !brief) return <div className="content-hub__error">{error}</div>;
  if (!brief) return null;

  const slides = brief.slides || [];
  const assets = brief.assets || [];
  const renders = brief.renders || [];
  const currentMood = MOOD_OPTIONS.find((m) => m.value === (brief.mood || "night-tempo"));

  // Progress: how many slides have copy filled (any non-empty string field in template_data)
  const filledCount = slides.filter((s) => {
    const data = s.template_data || {};
    return Object.values(data).some((v) => typeof v === "string" && v.trim().length > 0);
  }).length;
  const slideTotal = slides.length || brief.slide_count || 0;
  const hasRenders = renders.length > 0;

  return (
    <div className="content-hub__brief">
      {/* ─── Header ──────────────────────────────────────────────── */}
      <header className="bd__header">
        <div className="bd__header-top">
          <div className="bd__topic-block">
            {brief.variation_of && (
              <button
                type="button"
                className="bd__variation-parent"
                onClick={() => onOpenBrief?.(brief.parent?.id || brief.variation_of)}
                title="Open the original brief"
              >
                ↩ {brief.variation_label || "Variation"} of {brief.parent?.topic || "original brief"}
              </button>
            )}
            <h2 className="bd__topic">{brief.topic}</h2>
            <div className="bd__meta">
              <span className="content-hub__tag">{brief.pillar}</span>
              <span className="content-hub__tag">{brief.platform}</span>
              <span className="content-hub__tag content-hub__tag--soft">{brief.template}</span>
              <label className="content-hub__mood-inline" title="Visual mood — re-render to apply">
                <span
                  className="content-hub__mood-swatch"
                  style={{ background: currentMood?.swatch }}
                  aria-hidden="true"
                />
                <select
                  value={brief.mood || "night-tempo"}
                  onChange={(e) => handleMoodChange(e.target.value)}
                  disabled={busy === "saving"}
                >
                  {MOOD_OPTIONS.map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>
          <div className="bd__status-block">
            <span className={`bd__status-pill bd__status-pill--${brief.status}`}>
              {brief.status === "ready_to_render" ? "ready" : brief.status}
            </span>
            <div className="bd__progress" title={`${filledCount} of ${slideTotal} slides have copy`}>
              {filledCount}/{slideTotal} slides
              {hasRenders && <span className="bd__progress-rendered"> · {renders.length} rendered</span>}
            </div>
          </div>
        </div>

        {Array.isArray(brief.publications) && brief.publications.length > 0 && (
          <div className="bd__published" role="status">
            <span className="bd__published-dot" aria-hidden="true" />
            <span>
              <strong>Already published</strong> to{" "}
              {brief.publications.map((p, i) => {
                const when = fmtPubDate(p.posted_at || p.scheduled_at);
                return (
                  <span key={p.postbridge_account_id || i}>
                    {i > 0 ? ", " : ""}@{p.account_username || "account"}{when ? ` (${when})` : ""}
                  </span>
                );
              })}
              {" "}— don't re-post to the same account unless you mean to.
            </span>
          </div>
        )}

        <div className="bd__actions">
          <div className="bd__actions-left">
            {slides.length === 0 && (
              <button
                type="button"
                className="content-hub__btn"
                onClick={() => handleGenerate(false)}
                disabled={busy === "generating" || busy === "regenerating"}
              >
                Generate copy
              </button>
            )}
            {slides.length > 0 && (
              <button
                type="button"
                className="content-hub__btn"
                onClick={() => handleGenerate(true)}
                disabled={busy === "generating" || busy === "regenerating"}
                title="Regenerate all copy from scratch"
              >
                Regenerate
              </button>
            )}
            {slides.length > 0 && (
              <button
                type="button"
                className="content-hub__btn content-hub__btn--ghost"
                onClick={handleSaveSlides}
                disabled={busy === "saving"}
              >
                Save edits
              </button>
            )}
          </div>
          <div className="bd__actions-right">
            <button
              type="button"
              className="content-hub__btn content-hub__btn--primary"
              onClick={handleRender}
              disabled={busy === "rendering" || !slides.length}
            >
              {hasRenders ? "Re-render" : "Render to PNG"}
            </button>
            <button
              type="button"
              className="bd__icon-btn"
              onClick={handleDelete}
              title="Delete brief"
              aria-label="Delete brief"
              disabled={busy === "deleting"}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                <path d="M10 11v6M14 11v6" />
                <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
              </svg>
            </button>
          </div>
        </div>
      </header>

      {!brief.variation_of && (
        <section className="bd__variations">
          <div className="bd__variations-head">
            <div className="bd__variations-intro">
              <span className="bd__variations-title">Variations</span>
              <span className="bd__variations-sub">
                Linked copies with reworded text + a different mood — one per account, so the same idea doesn't post as a duplicate.
              </span>
            </div>
            <div className="bd__variations-gen">
              <label className="bd__variations-count">
                <span>How many</span>
                <select
                  value={variationCount}
                  onChange={(e) => setVariationCount(Number(e.target.value))}
                  disabled={busy === "variations"}
                >
                  {[2, 3, 4, 5, 6].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="content-hub__btn content-hub__btn--primary"
                onClick={handleGenerateVariations}
                disabled={busy === "variations" || filledCount === 0}
                title={filledCount === 0 ? "Generate the brief's copy first" : "Generate variations"}
              >
                {busy === "variations" ? "Generating…" : "✦ Generate variations"}
              </button>
              {Array.isArray(brief.variations) && brief.variations.length > 0 && (
                <button
                  type="button"
                  className="content-hub__btn"
                  onClick={spreadOpen ? () => setSpreadOpen(false) : openSpread}
                  disabled={busy === "variations" || spreadBusy}
                  title="Post one variation per account"
                >
                  {spreadOpen ? "Close spread" : "⇄ Spread to accounts"}
                </button>
              )}
            </div>
          </div>
          {varMsg && <div className="bd__variations-msg">{varMsg}</div>}
          {Array.isArray(brief.variations) && brief.variations.length > 0 && (
            <div className="bd__variations-grid">
              {brief.variations.map((v) => {
                const vp = Array.isArray(v.publications) ? v.publications : [];
                return (
                  <button
                    key={v.id}
                    type="button"
                    className="bd__variation-card"
                    onClick={() => onOpenBrief?.(v.id)}
                    title="Open this variation"
                  >
                    <div
                      className="bd__variation-thumb"
                      style={{ background: MOOD_BY_VALUE[v.mood]?.swatch || "#2a2d3a" }}
                    >
                      {v.thumb ? <img src={v.thumb} alt="" loading="lazy" /> : <span>{v.variation_label || "V"}</span>}
                    </div>
                    <div className="bd__variation-meta">
                      <span className="bd__variation-label">{v.variation_label || "Variation"}</span>
                      <span className="bd__variation-mood">{MOOD_BY_VALUE[v.mood]?.label || v.mood}</span>
                      {vp.length > 0 && (
                        <span
                          className="bd__variation-posted"
                          title={`Posted to ${vp.map((p) => `@${p.account_username || "?"}`).join(", ")}`}
                        >
                          ● Posted
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {spreadOpen && (
            <div className="bd__spread">
              {loadingSpread ? (
                <p className="bd__spread-muted">Loading accounts…</p>
              ) : spreadAccounts.length === 0 ? (
                <p className="bd__spread-muted">No connected accounts found. Connect one in Studio first.</p>
              ) : (
                <>
                  <div className="bd__spread-head">
                    <span className="bd__spread-title">Spread across accounts</span>
                    <span className="bd__spread-sub">One variation per account — each posts its own distinct copy + caption.</span>
                    <label className="bd__spread-mode" title="Post the 9:16 motion reel (music baked in) instead of the image carousel. Missing reels are built automatically, auto-picking a track by each variation's mood.">
                      <input
                        type="checkbox"
                        checked={spreadAsReel}
                        onChange={(e) => setSpreadAsReel(e.target.checked)}
                        disabled={spreadBusy}
                      />
                      <span>🎵 Post as Reel (with music){spreadAsReel ? " — builds any missing reels" : ""}</span>
                    </label>
                  </div>
                  <div className="bd__spread-rows">
                    {spreadUnits.map((u) => {
                      const already = (u.publications || [])
                        .filter((p) => String(p.postbridge_account_id) === String(u.account_id))
                        .length > 0;
                      return (
                        <div key={u.id} className="bd__spread-row">
                          <div className="bd__spread-unit">
                            <span className="bd__spread-swatch" style={{ background: MOOD_BY_VALUE[u.mood]?.swatch || "#2a2d3a" }} aria-hidden="true" />
                            <span className="bd__spread-unit-label">{u.label}</span>
                            {spreadAsReel ? (
                              u.reel?.url ? (
                                <span className="bd__spread-flag">reel ✓</span>
                              ) : (
                                <span className="bd__spread-flag bd__spread-flag--warn">no reel — will build</span>
                              )
                            ) : (
                              !u.renders.length && <span className="bd__spread-flag bd__spread-flag--warn">no renders</span>
                            )}
                            {already && <span className="bd__spread-flag">already posted here</span>}
                          </div>
                          <span className="bd__spread-arrow">→</span>
                          <select
                            className="bd__spread-select"
                            value={u.account_id}
                            onChange={(e) => setUnitAccount(u.id, e.target.value)}
                            disabled={spreadBusy}
                          >
                            <option value="">— skip —</option>
                            {spreadAccounts.map((a) => (
                              <option key={a.id} value={a.id}>@{a.username}</option>
                            ))}
                          </select>
                        </div>
                      );
                    })}
                  </div>
                  <div className="bd__spread-actions">
                    <button
                      type="button"
                      className="content-hub__btn content-hub__btn--primary"
                      onClick={doSpread}
                      disabled={spreadBusy}
                    >
                      {spreadBusy ? "Posting…" : "⇄ Post spread"}
                    </button>
                    <span className="bd__spread-note">
                      Posts {spreadAsReel ? "reels (with music)" : "carousels"} to real Instagram accounts.
                    </span>
                  </div>
                  {spreadResults && (
                    <div className="bd__spread-results">
                      {spreadResults.map((r, i) => (
                        <div key={i} className={`bd__spread-result bd__spread-result--${r.status}`}>
                          <span className="bd__spread-result-dot" />
                          <span>{r.unit} → @{r.username}</span>
                          <span className="bd__spread-result-status">{r.status === "failed" ? (r.error || "failed") : r.status}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {hasRenders && (
        <section className="bd__reel">
          <div className="bd__reel-head">
            <div className="bd__reel-intro">
              <span className="bd__reel-title">Motion reel</span>
              <span className="bd__reel-sub">
                A 9:16 video from these slides — Ken-Burns + crossfades, free (no AI credits). Post it as a Reel/TikTok.
              </span>
            </div>
            <div className="bd__reel-actions">
              <label
                className="bd__reel-music"
                title="Music is baked into the MP4 (Instagram/TikTok can't add a sound after the API posts it). Auto picks a royalty-free track by the brief's mood; variations rotate so multi-account posts differ."
              >
                <span className="bd__reel-music-label">♪ Music</span>
                <select
                  value={audioTrack}
                  onChange={(e) => setAudioTrack(e.target.value)}
                  disabled={busy === "reel"}
                >
                  <option value="auto">Auto (by mood)</option>
                  <option value="none">No music</option>
                  {audioVibes.map((v) => (
                    <optgroup key={v.name} label={v.label}>
                      {v.tracks.map((t) => (
                        <option key={t.id} value={t.id}>{t.label}</option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className={`bd__reel-play${previewId === audioTrack ? " is-playing" : ""}`}
                onClick={togglePreview}
                disabled={!audioTrack || audioTrack === "auto" || audioTrack === "none"}
                title="Preview the selected track's hook"
                aria-label="Preview track"
              >
                {previewId === audioTrack && audioTrack !== "auto" && audioTrack !== "none" ? "⏸" : "▶︎"}
              </button>
              <button
                type="button"
                className="content-hub__btn content-hub__btn--primary"
                onClick={handleMakeReel}
                disabled={busy === "reel"}
              >
                {busy === "reel" ? "Building…" : brief.reel ? "↻ Rebuild reel" : "✦ Make reel"}
              </button>
              <audio ref={audioElRef} onEnded={() => setPreviewId(null)} hidden />
            </div>
          </div>
          {reelMsg && <div className="bd__reel-msg">{reelMsg}</div>}
          {brief.reel?.url && (
            <div className="bd__reel-preview">
              <video className="bd__reel-video" src={brief.reel.url} controls playsInline loop preload="metadata" />
              <div className="bd__reel-meta">
                <span>
                  {brief.reel.slide_count} slides · {brief.reel.duration_s}s · {brief.reel.width}×{brief.reel.height}
                  {brief.reel.audio_label ? ` · ♪ ${brief.reel.audio_label}` : " · silent"}
                </span>
                <div className="bd__reel-post">
                  <select
                    className="bd__spread-select"
                    value={reelAccountId}
                    onChange={(e) => setReelAccountId(e.target.value)}
                    disabled={reelBusy || reelAccounts.length === 0}
                    aria-label="Account to post the reel to"
                  >
                    <option value="">{reelAccounts.length ? "Post to account…" : "No connected accounts"}</option>
                    {reelAccounts.map((a) => (
                      <option key={a.id} value={a.id}>@{a.username}{a.platform ? ` · ${a.platform}` : ""}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="content-hub__btn content-hub__btn--primary"
                    onClick={handlePostReel}
                    disabled={reelBusy || !reelAccountId}
                  >
                    {reelBusy ? "Posting…" : "Post reel"}
                  </button>
                </div>
                <span className="bd__reel-postnote">Posts as a Reel to a real Instagram account.</span>
                <a className="content-hub__btn content-hub__btn--ghost" href={brief.reel.url} download target="_blank" rel="noreferrer">
                  Download MP4
                </a>
              </div>
            </div>
          )}
        </section>
      )}

      {(() => {
        const lc = brief.live_context;
        const hasTarget =
          brief.target_city_id || brief.target_club_id || brief.target_meeting_id || brief.target_date_range;
        // Always render the panel — it carries the city re-target control, so even
        // an untargeted brief can be pointed at a city and regenerated.
        const ageHrs = hoursSince(brief.live_context_at);
        const stale = lc && ageHrs != null && ageHrs > STALE_HOURS;
        const clubNames = [
          ...(lc?.club ? [lc.club.name] : []),
          ...((lc?.clubs || []).map((c) => c.name)),
        ].filter(Boolean);
        const runs = [
          ...(lc?.meeting ? [lc.meeting] : []),
          ...(lc?.upcoming_runs || []),
        ];
        return (
          <div className={`bd__livedata ${stale ? "is-stale" : ""}`}>
            <div className="bd__livedata-head">
              <span className="bd__livedata-title">◆ Live data</span>
              <label className="bd__livedata-city">
                <span>City</span>
                <select
                  value={brief.target_city_id || ""}
                  onChange={(e) => handleCityTargetChange(e.target.value)}
                  disabled={busy === "saving"}
                >
                  <option value="">— None —</option>
                  {cities.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </label>
              {lc && (
                <span className="bd__livedata-age" title={brief.live_context_at}>
                  pulled {humanAge(ageHrs)} ago
                </span>
              )}
            </div>
            {stale && (
              <div className="bd__livedata-warn">
                ⚠ Live data is {humanAge(ageHrs)} old — dates may have passed.
                <button
                  type="button"
                  className="content-hub__btn content-hub__btn--link"
                  onClick={() => handleGenerate(true)}
                  disabled={busy === "regenerating" || busy === "generating"}
                >
                  Regenerate to refresh
                </button>
              </div>
            )}
            {!lc && hasTarget && (
              <div className="bd__livedata-hint">
                City set — click <strong>Regenerate</strong> to pull real clubs &amp; runs into the copy.
              </div>
            )}
            {!lc && !hasTarget && (
              <div className="bd__livedata-hint">
                Pick a city to ground this brief in real clubs &amp; runs, then <strong>Regenerate</strong>. Without it, copy stays generic.
              </div>
            )}
            {clubNames.length > 0 && (
              <div className="bd__livedata-row">
                <span className="bd__livedata-label">Clubs</span>
                <span className="bd__livedata-vals">{clubNames.slice(0, 8).join(" · ")}</span>
              </div>
            )}
            {runs.length > 0 && (
              <div className="bd__livedata-row">
                <span className="bd__livedata-label">Runs</span>
                <span className="bd__livedata-vals">
                  {runs.slice(0, 5).map((r, i) => (
                    <span key={i} className="bd__livedata-run">
                      {[r.club, r.when].filter(Boolean).join(" ")}
                    </span>
                  ))}
                </span>
              </div>
            )}
          </div>
        );
      })()}

      {busy && <div className="content-hub__busy">…{busy}</div>}
      {error && <div className="content-hub__error">{error}</div>}

      {/* ─── Body sub-tabs ────────────────────────────────────────── */}
      <div className="bd__tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={bodyTab === "copy"}
          className={`bd__tab ${bodyTab === "copy" ? "is-active" : ""}`}
          onClick={() => setBodyTab("copy")}
        >
          Copy
          <span className="bd__tab-count">{filledCount}/{slideTotal}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={bodyTab === "assets"}
          className={`bd__tab ${bodyTab === "assets" ? "is-active" : ""}`}
          onClick={() => setBodyTab("assets")}
        >
          Assets
          {assets.length > 0 && <span className="bd__tab-count">{assets.length}</span>}
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={bodyTab === "renders"}
          className={`bd__tab ${bodyTab === "renders" ? "is-active" : ""}`}
          onClick={() => setBodyTab("renders")}
        >
          Renders
          {renders.length > 0 && <span className="bd__tab-count">{renders.length}</span>}
        </button>
      </div>

      {bodyTab === "copy" && (
        <div className="bd__pane">
          <section className="bd__block">
            <div className="bd__block-head">
              <h3 className="bd__block-title">Slides</h3>
              <div className="bd__block-head-right">
                {(brief.higgsfield_prompts || []).some((p) => (p || "").trim()) && (
                  <button
                    type="button"
                    className="bd__higgs-gen bd__higgs-gen--all"
                    onClick={handleGenerateAllImages}
                    disabled={typeof busy === "string" && busy.startsWith("gen-all")}
                    title="Generate a Higgsfield image for every slide that has a prompt, and attach each to its slide"
                  >
                    {typeof busy === "string" && busy.startsWith("gen-all")
                      ? `Generating ${busy.replace("gen-all ", "")}…`
                      : "✦ Generate all images"}
                  </button>
                )}
                <div className="bd__block-meta">{filledCount} of {slideTotal} have copy</div>
              </div>
            </div>
            {slides.length === 0 ? (
              <p className="content-hub__hint">No slides yet — click "Generate copy" above to have Claude fill them.</p>
            ) : (
              <div className="bd__slide-grid">
                {slides.map((s, idx) => {
                  const data = s.template_data || {};
                  const keys = Object.keys(data).filter((k) => typeof data[k] === "string");
                  const isFilled = keys.some((k) => (data[k] || "").trim().length > 0);
                  const attachedAsset = assets.find((a) => a.id === s.asset_id);
                  const attachedUrl = attachedAsset?.url || data.screenshot || data.bg || null;
                  const higgs = (brief.higgsfield_prompts || [])[idx] || "";
                  const slideNo = s.index || idx + 1;
                  const slideRender = renders.find((r) => r.slide_index === slideNo);
                  const isRenderingSlide = busy === `render-slide-${slideNo}`;
                  return (
                    <div key={idx} className={`bd__slide ${isFilled ? "is-filled" : "is-empty"}`}>
                      <div className="bd__slide-rail">
                        <div className="bd__slide-index">{s.index || idx + 1}</div>
                        <div
                          className="bd__slide-thumb"
                          style={attachedUrl ? { backgroundImage: `url(${attachedUrl})` } : undefined}
                          title={attachedAsset ? `Attached: ${attachedAsset.label || attachedAsset.kind}` : "No asset"}
                        >
                          {!attachedUrl && <span className="bd__slide-thumb-empty">no asset</span>}
                        </div>
                        {data.bg && (
                          <div className="bd__slide-animate-row" style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                            <button
                              type="button"
                              className="bd__slide-animate"
                              onClick={() => handleAnimateSlide(slideNo, 5)}
                              disabled={busy === `animate-${slideNo}`}
                              title="Animate this hero into a ~5s cinematic clip (Higgsfield, ~7 credits)"
                            >
                              {busy === `animate-${slideNo}` ? "Animating…" : data.clip ? "↻ Re-animate" : "✦ Animate"}
                            </button>
                            <button
                              type="button"
                              className="bd__slide-animate"
                              style={{ fontSize: "0.85em", opacity: 0.85 }}
                              onClick={() => handleAnimateSlide(slideNo, 10)}
                              disabled={busy === `animate-${slideNo}`}
                              title="Animate into a ~10s clip — about 2× the credits"
                            >
                              10s
                            </button>
                          </div>
                        )}
                        {data.clip && (
                          <video className="bd__slide-clip" src={data.clip} controls loop playsInline preload="metadata" />
                        )}
                        {animateMsg[slideNo] && (
                          <div className={`bd__slide-render-msg ${animateMsg[slideNo].ok ? "is-ok" : "is-err"}`}>
                            {animateMsg[slideNo].text}
                          </div>
                        )}
                      </div>
                      <div className="bd__slide-body">
                        <div className="bd__slide-row">
                          <div className="bd__slide-status">
                            {isFilled
                              ? <span className="bd__chip bd__chip--ok">copy ready</span>
                              : <span className="bd__chip bd__chip--warn">empty</span>}
                          </div>
                          {assets.length > 0 && (
                            <label className="bd__inline-field">
                              <span>asset</span>
                              <select
                                value={s.asset_id || ""}
                                onChange={(e) => handleAttachAssetToSlide(idx, e.target.value)}
                              >
                                <option value="">— none —</option>
                                {assets.map((a) => (
                                  <option key={a.id} value={a.id}>{a.label || a.kind}</option>
                                ))}
                              </select>
                            </label>
                          )}
                        </div>
                        {keys.map((k) => {
                          const isImageField = k === "bg" || k === "screenshot" || k === "screenshot2";
                          const isRegen = busy === `field-${slideNo}-${k}`;
                          return (
                            <label key={k} className="content-hub__slide-field">
                              <span className="bd__field-head">
                                <span>{k}</span>
                                {!isImageField && (
                                  <button
                                    type="button"
                                    className="bd__field-ai"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      handleRegenerateField(slideNo, k);
                                    }}
                                    disabled={isRegen}
                                    title={`Regenerate this ${k} with AI`}
                                  >
                                    {isRegen ? "…" : "✦ AI"}
                                  </button>
                                )}
                              </span>
                              {data[k] && data[k].length > 80 ? (
                                <textarea
                                  value={data[k]}
                                  rows={2}
                                  onChange={(e) => handleSlideEdit(idx, k, e.target.value)}
                                />
                              ) : (
                                <input
                                  type="text"
                                  value={data[k] || ""}
                                  onChange={(e) => handleSlideEdit(idx, k, e.target.value)}
                                />
                              )}
                            </label>
                          );
                        })}
                        {higgs && (
                          <details className="bd__higgs">
                            <summary>
                              Higgsfield prompt
                              <button
                                type="button"
                                className="bd__higgs-gen"
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleGenerateImage(slideNo, higgs);
                                }}
                                disabled={busy === `gen-image-${slideNo}`}
                                title="Generate this image with Higgsfield (~0.12 credits) and attach it to the slide"
                              >
                                {busy === `gen-image-${slideNo}` ? "Generating…" : "✦ Generate image"}
                              </button>
                            </summary>
                            <p>{higgs}</p>
                          </details>
                        )}
                        <div className="bd__slide-render">
                          {slideRender ? (
                            <button
                              type="button"
                              className="bd__slide-render-thumb"
                              style={{ backgroundImage: `url(${slideRender.url})` }}
                              onClick={() => setLightboxRender(slideRender)}
                              title="View this slide's current render"
                              aria-label={`View render for slide ${slideNo}`}
                            />
                          ) : (
                            <span className="bd__slide-render-none">not rendered</span>
                          )}
                          <button
                            type="button"
                            className="bd__slide-save"
                            onClick={() => handleSaveSlide(slideNo)}
                            disabled={busy === `save-slide-${slideNo}`}
                            title="Save this slide's copy edits"
                          >
                            {busy === `save-slide-${slideNo}` ? "Saving…" : "Save copy"}
                          </button>
                          <button
                            type="button"
                            className="bd__slide-rerender"
                            onClick={() => handleRenderSlide(slideNo)}
                            disabled={!isFilled || isRenderingSlide}
                            title="Re-render only this slide to PNG and drop it into its slot — other slides are untouched"
                          >
                            {isRenderingSlide
                              ? "Rendering…"
                              : slideRender
                                ? "↻ Re-render slide"
                                : "⧉ Render slide"}
                          </button>
                          {renderMsg[slideNo] && (
                            <span
                              className={`bd__slide-render-msg ${renderMsg[slideNo].ok ? "is-ok" : "is-err"}`}
                              role="status"
                            >
                              {renderMsg[slideNo].text}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <section className="bd__block">
            <div className="bd__block-head">
              <h3 className="bd__block-title">Captions</h3>
              <div className="bd__caption-tabs" role="tablist">
                {[
                  { value: "instagram", label: "Instagram", limit: 2200 },
                  { value: "facebook", label: "Facebook", limit: 2200 },
                  { value: "tiktok", label: "TikTok", limit: 2200 },
                ].map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    role="tab"
                    aria-selected={captionTab === p.value}
                    className={`bd__caption-tab ${captionTab === p.value ? "is-active" : ""}`}
                    onClick={() => setCaptionTab(p.value)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            {(() => {
              const text = (brief.captions || {})[captionTab] || "";
              const limit = 2200;
              const remaining = limit - text.length;
              return (
                <div className="bd__caption-editor">
                  <textarea
                    value={text}
                    rows={6}
                    onChange={(e) => handleCaptionEdit(captionTab, e.target.value)}
                    placeholder={`Caption for ${captionTab}…`}
                  />
                  <div className={`bd__caption-count ${remaining < 100 ? "is-warn" : ""}`}>
                    {text.length} / {limit}
                  </div>
                </div>
              );
            })()}
            {(brief.captions?.hashtags?.length || 0) > 0 && (
              <div className="bd__hashtag-row">
                <span className="bd__hashtag-label">Hashtags</span>
                <div className="bd__hashtag-chips">
                  {brief.captions.hashtags.map((h, i) => (
                    <button
                      key={i}
                      type="button"
                      className="bd__hashtag"
                      onClick={() => navigator.clipboard?.writeText(`#${h}`)}
                      title={`Click to copy #${h}`}
                    >
                      #{h}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="content-hub__btn content-hub__btn--ghost bd__hashtag-copy-all"
                    onClick={() => {
                      const joined = brief.captions.hashtags.map((h) => `#${h}`).join(" ");
                      navigator.clipboard?.writeText(joined);
                    }}
                  >
                    Copy all
                  </button>
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {bodyTab === "assets" && (
      <div className="bd__pane">
      <section className="content-hub__section">
        <header className="content-hub__section-header">
          <h3>Stock library</h3>
          {stockLib?.total > 0 && (
            <span className="content-hub__section-meta">
              {stockLib.total} curated asset{stockLib.total === 1 ? "" : "s"}
            </span>
          )}
        </header>
        {!stockLib || stockLib.total === 0 ? (
          <p className="content-hub__hint">
            Drop photos in <code>marketing/stock/&lt;category&gt;/</code> to populate. See <code>marketing/stock/README.md</code>.
          </p>
        ) : (
          <>
            <div className="content-hub__stock-tabs">
              {stockLib.categories.map((cat) => (
                <button
                  key={cat.name}
                  type="button"
                  className={`content-hub__stock-tab ${stockCategory === cat.name ? "is-active" : ""}`}
                  onClick={() => setStockCategory(cat.name)}
                >
                  {cat.label} <span className="content-hub__stock-tab-count">{cat.count}</span>
                </button>
              ))}
            </div>
            {pendingStockUrl && (
              <div className="content-hub__stock-picker">
                <div className="content-hub__stock-picker-text">
                  Attach <strong>{pendingStockLabel}</strong> to which slide?
                </div>
                <div className="content-hub__stock-picker-actions">
                  {slides.map((s, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className="content-hub__btn"
                      onClick={() => handleAttachStockToSlide(idx)}
                    >
                      Slide {s.index || idx + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    className="content-hub__btn content-hub__btn--link"
                    onClick={cancelStockAttach}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            <div className="content-hub__stock-grid">
              {(stockLib.categories.find((c) => c.name === stockCategory)?.items || []).map((it) => {
                const isUsed = slides.some(
                  (s) => s.template_data?.screenshot === it.url || s.template_data?.bg === it.url
                );
                return (
                  <div
                    key={it.url}
                    className={`content-hub__stock-card ${isUsed ? "is-used" : ""}`}
                  >
                    <button
                      type="button"
                      className="content-hub__stock-card-clickable"
                      onClick={() => handleStockClick(it)}
                      title={`${it.label} · click to attach`}
                    >
                      <img src={it.url} alt={it.label} loading="lazy" />
                      {isUsed && <div className="content-hub__stock-used-badge">Attached</div>}
                      <div className="content-hub__stock-card-label">{it.label}</div>
                    </button>
                    <button
                      type="button"
                      className="content-hub__card-dl"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadImageFromUrl(it.url, it.filename || `${it.label}.png`);
                      }}
                      title="Download"
                      aria-label="Download"
                    >
                      ↓
                    </button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </section>

      <section className="content-hub__section">
        <header className="content-hub__section-header">
          <h3>Assets</h3>
        </header>
        <div className="content-hub__upload">
          <label className="content-hub__btn content-hub__btn--ghost">
            Upload Higgsfield / photo…
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleUpload(f);
                e.target.value = "";
              }}
            />
          </label>
          <div className="content-hub__capture">
            <select
              value={captureRoute}
              onChange={(e) => setCaptureRoute(e.target.value)}
            >
              {KNOWN_ROUTES.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            <select
              value={`${captureViewport.w}x${captureViewport.h}`}
              onChange={(e) => {
                const [w, h] = e.target.value.split("x").map(Number);
                setCaptureViewport({ label: e.target.value, w, h });
              }}
            >
              {PLATFORM_VIEWPORTS.map((v) => (
                <option key={v.label} value={`${v.w}x${v.h}`}>{v.label}</option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Label (optional)"
              value={captureLabel}
              onChange={(e) => setCaptureLabel(e.target.value)}
            />
            <button
              type="button"
              className="content-hub__btn"
              onClick={handleCapture}
              disabled={busy === "capturing"}
            >
              Capture screenshot
            </button>
          </div>
        </div>

        {/* Capture from the LIVE mobile app (iOS Simulator) */}
        {(() => {
          const ms = mobileStatus;
          const ready = !!(ms && ms.available && ms.booted && ms.app_installed);
          const phoneSlides = (brief?.slides || [])
            .filter((s) => s.template_data && "screenshot" in s.template_data)
            .map((s) => ({ index: s.index }));
          const hasMeeting = !!brief?.target_meeting_id;
          const hasClub = !!brief?.target_club_id;
          const autoLabel = hasMeeting
            ? "Auto — this brief's run"
            : hasClub
            ? "Auto — this brief's club"
            : "Auto — Discover feed";
          let statusMsg = null;
          if (!ms) statusMsg = "Checking simulator…";
          else if (!ms.available) statusMsg = "Needs a Mac with Xcode — not available here.";
          else if (!ms.booted) statusMsg = "No simulator booted. Run “npm run ios” in mobile/, then Recheck.";
          else if (!ms.app_installed) statusMsg = "RunCards app not installed. Run “npm run ios” in mobile/, then Recheck.";

          return (
            <div className="content-hub__mobile-capture">
              <div className="bd__mobcap-head">
                <span className="bd__mobcap-title">▣ Capture from the live mobile app</span>
                <span
                  className={`bd__mobcap-dot ${ready ? "is-ready" : "is-off"}`}
                  title={ready ? `Ready — ${ms.device || "simulator"}` : "Unavailable"}
                />
                {ready && ms.device && <span className="bd__mobcap-device">{ms.device}</span>}
              </div>
              {statusMsg ? (
                <div className="bd__mobcap-status">
                  <span>{statusMsg}</span>
                  <button
                    type="button"
                    className="content-hub__btn content-hub__btn--ghost"
                    onClick={refreshMobileStatus}
                  >
                    Recheck
                  </button>
                </div>
              ) : (
                <div className="content-hub__capture">
                  <select value={mobileScreen} onChange={(e) => setMobileScreen(e.target.value)}>
                    <option value="auto">{autoLabel}</option>
                    {hasClub && <option value="club">Club card (brief’s club)</option>}
                    {hasMeeting && <option value="meeting">Run details (brief’s run)</option>}
                    <option value="discover">Discover feed</option>
                    <option value="runs">Runs tab</option>
                    <option value="map">Runs map view</option>
                    <option value="connections">Connections</option>
                  </select>
                  <select
                    value={mobileAttachSlide}
                    onChange={(e) => setMobileAttachSlide(e.target.value)}
                  >
                    <option value="">Add to bank only</option>
                    {phoneSlides.map((p) => (
                      <option key={p.index} value={p.index}>
                        ↳ Attach to slide {p.index}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="content-hub__btn"
                    onClick={handleCaptureMobile}
                    disabled={busy === "capturing-mobile" || busy === "capturing-mobile-set"}
                  >
                    {busy === "capturing-mobile" ? "Capturing…" : "Capture from app"}
                  </button>
                  <button
                    type="button"
                    className="content-hub__btn content-hub__btn--primary"
                    onClick={handleCaptureMobileSet}
                    disabled={busy === "capturing-mobile" || busy === "capturing-mobile-set"}
                    title="Capture a set of relevant screens (club card, runs map, discover) in one pass"
                  >
                    {busy === "capturing-mobile-set" ? "Capturing set…" : "Capture set"}
                  </button>
                </div>
              )}
              <p className="content-hub__hint bd__mobcap-hint">
                Opens the screen in the booted simulator and screenshots it — saved to this
                brief’s assets and the reusable screenshots bank.
              </p>
            </div>
          );
        })()}

        {/* Screenshots bank picker */}
        <div className="bd__bank">
          <button
            type="button"
            className="bd__bank-toggle"
            onClick={handleOpenBank}
            aria-expanded={bankOpen}
          >
            <span className="bd__bank-chev" data-open={bankOpen}>▾</span>
            Browse screenshots bank
            {bankItems !== null && (
              <span className="bd__bank-count">{bankItems.length}</span>
            )}
          </button>
          {bankOpen && (
            <div className="bd__bank-panel">
              {bankItems === null ? (
                <p className="content-hub__hint">Loading bank…</p>
              ) : bankItems.length === 0 ? (
                <p className="content-hub__hint">
                  Your screenshots bank is empty. Add some on the Screenshots tab, then come back here.
                </p>
              ) : (
                <>
                  {(() => {
                    const cats = Array.from(new Set(bankItems.map((b) => b.category || "general")));
                    return cats.length > 1 ? (
                      <div className="bd__bank-cats">
                        <button
                          type="button"
                          className={`bd__bank-cat ${!bankCategory ? "is-active" : ""}`}
                          onClick={() => setBankCategory("")}
                        >
                          All <span>{bankItems.length}</span>
                        </button>
                        {cats.map((c) => (
                          <button
                            key={c}
                            type="button"
                            className={`bd__bank-cat ${bankCategory === c ? "is-active" : ""}`}
                            onClick={() => setBankCategory(c)}
                          >
                            {c} <span>{bankItems.filter((b) => (b.category || "general") === c).length}</span>
                          </button>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  <div className="bd__bank-grid">
                    {bankItems
                      .filter((b) => !bankCategory || (b.category || "general") === bankCategory)
                      .map((b) => {
                        const alreadyAttached = assets.some((a) => a.url === b.url);
                        return (
                          <button
                            key={b.id}
                            type="button"
                            className={`bd__bank-card ${alreadyAttached ? "is-attached" : ""}`}
                            onClick={() => handleAttachFromBank(b.id)}
                            disabled={alreadyAttached || busy === "attaching"}
                            title={alreadyAttached ? "Already attached to this brief" : `Attach ${b.label}`}
                          >
                            <div className="bd__bank-thumb">
                              <img src={b.url} alt={b.label} loading="lazy" />
                              {alreadyAttached && <span className="bd__bank-badge">attached</span>}
                            </div>
                            <div className="bd__bank-label">{b.label}</div>
                          </button>
                        );
                      })}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {assets.length > 0 && (
          <div className="content-hub__asset-grid">
            {assets.map((a) => (
              <div key={a.id} className="content-hub__asset">
                <img src={a.url} alt={a.label || a.kind} />
                <button
                  type="button"
                  className="content-hub__card-dl"
                  onClick={() => {
                    const safeLabel = (a.label || a.kind || "asset")
                      .toLowerCase()
                      .replace(/[^\w\s-]/g, "")
                      .replace(/[-\s]+/g, "-");
                    downloadImageFromUrl(a.url, `runcards-asset-${safeLabel}-${a.id.slice(0, 8)}.png`);
                  }}
                  title="Download"
                  aria-label="Download"
                >
                  ↓
                </button>
                <div className="content-hub__asset-meta">
                  <span className="content-hub__tag">{a.kind}</span>
                  <span title={a.label}>{a.label || a.id.slice(0, 8)}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      </div>
      )}

      {bodyTab === "renders" && (
      <div className="bd__pane">
      <section className="content-hub__section">
        <header className="content-hub__section-header">
          <h3>Rendered slides</h3>
          {renders.length > 0 && (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <span className="content-hub__section-meta">
                {renders[0]?.width}×{renders[0]?.height} · IG carousel ready
              </span>
              <button
                type="button"
                className="content-hub__btn content-hub__btn--primary"
                onClick={() =>
                  runWithBusy("downloading zip", async () => {
                    const { blob, filename } = await consoleApi.content.downloadRendersZip(briefId);
                    triggerBlobDownload(blob, filename);
                  })
                }
                disabled={busy === "downloading zip"}
                style={{ marginLeft: 8 }}
              >
                ↓ Download all ({renders.length})
              </button>
            </div>
          )}
        </header>
        {renders.length === 0 ? (
          <p className="content-hub__hint">Click "Render to PNG" once slide copy + assets are set.</p>
        ) : (
          <div className="content-hub__renders">
            {[...renders]
              .sort((a, b) => a.slide_index - b.slide_index)
              .map((r) => (
                <div
                  key={r.id}
                  className="content-hub__render"
                  title={`${r.width}x${r.height}`}
                >
                  <button
                    type="button"
                    className="bd__render-thumb-btn"
                    onClick={() => setLightboxRender(r)}
                    title="Click to preview"
                  >
                    <img src={r.url} alt={`slide ${r.slide_index}`} />
                  </button>
                  <div className="content-hub__render-footer">
                    <div className="content-hub__render-label">
                      Slide {r.slide_index} · {r.width}×{r.height}
                    </div>
                    <button
                      type="button"
                      className="content-hub__render-dl"
                      onClick={() =>
                        runWithBusy(`downloading slide ${r.slide_index}`, async () => {
                          const { blob, filename } = await consoleApi.content.downloadRender(
                            briefId,
                            r.slide_index,
                          );
                          triggerBlobDownload(blob, filename);
                        })
                      }
                      disabled={busy === `downloading slide ${r.slide_index}`}
                      title="Download this slide as PNG"
                    >
                      ↓ PNG
                    </button>
                  </div>
                </div>
              ))}
          </div>
        )}
      </section>
      </div>
      )}

      {lightboxRender && (
        <div className="ch-lib__lightbox" onClick={() => setLightboxRender(null)}>
          <div className="ch-lib__lightbox-inner" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxRender.url} alt={`slide ${lightboxRender.slide_index}`} />
            <div className="ch-lib__lightbox-meta">
              <div className="ch-lib__lightbox-topic">Slide {lightboxRender.slide_index}</div>
              <div className="ch-lib__lightbox-tags">
                <span className="content-hub__tag">{brief.pillar}</span>
                <span className="content-hub__tag">{brief.platform}</span>
                <span className="content-hub__tag content-hub__tag--soft">
                  {lightboxRender.width}×{lightboxRender.height}
                </span>
              </div>
              <div className="ch-lib__lightbox-actions">
                <button
                  type="button"
                  className="content-hub__btn content-hub__btn--primary"
                  onClick={() =>
                    runWithBusy(`downloading slide ${lightboxRender.slide_index}`, async () => {
                      const { blob, filename } = await consoleApi.content.downloadRender(
                        briefId,
                        lightboxRender.slide_index,
                      );
                      triggerBlobDownload(blob, filename);
                    })
                  }
                >
                  ↓ Download PNG
                </button>
                <a
                  href={lightboxRender.url}
                  target="_blank"
                  rel="noreferrer"
                  className="content-hub__btn"
                >
                  Open original
                </a>
                <button
                  type="button"
                  className="content-hub__btn"
                  onClick={() => setLightboxRender(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
